import styled from 'styled-components';

const purple = '#4d258d';
const purpleLight = '#f3eff9';
const text = '#28252d';
const muted = '#6b6872';
const border = '#e2dfe7';

export const Page = styled.div`
  min-height: 100vh;
  box-sizing: border-box;
  padding: 24px 20px 56px;
  background: #f7f6f8;
  color: ${text};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

export const Header = styled.header`
  width: 100%;
  max-width: 1120px;
  margin: 0 auto 24px;
  padding-bottom: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  border-bottom: 1px solid ${border};

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const BrandGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
`;

export const Logo = styled.img`
  width: auto;
  height: 34px;
  max-width: 180px;
  object-fit: contain;
`;

export const Divider = styled.span`
  width: 1px;
  height: 32px;
  background: ${border};
`;

export const CreditLogo = styled.img`
  width: 132px;
  height: 38px;
  object-fit: contain;
`;

export const HeaderMeta = styled.div`
  color: ${muted};
  font-size: 13px;
  text-align: right;

  strong {
    display: block;
    margin-bottom: 3px;
    color: ${text};
    font-size: 15px;
  }

  @media (max-width: 640px) {
    text-align: left;
  }
`;

export const Layout = styled.main`
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 20px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const Section = styled.section`
  background: #fff;
  border: 1px solid ${border};
  border-radius: 8px;
  padding: 22px;
  box-sizing: border-box;
`;

export const SectionHeader = styled.div`
  margin-bottom: 18px;
  h1,
  h2 {
    margin: 0;
    color: ${text};
  }
  h1 {
    font-size: 22px;
    line-height: 1.25;
  }
  h2 {
    font-size: 17px;
    line-height: 1.3;
  }
  p {
    margin: 7px 0 0;
    color: ${muted};
    font-size: 14px;
    line-height: 1.45;
  }
`;

export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

export const Field = styled.label`
  display: block;
  min-width: 0;
  color: ${text};
  font-size: 14px;
  font-weight: 600;

  small {
    display: block;
    margin-top: 5px;
    color: ${muted};
    font-size: 12px;
    font-weight: 400;
    line-height: 1.35;
  }
`;

export const Input = styled.input`
  display: block;
  width: 100%;
  height: 42px;
  box-sizing: border-box;
  margin-top: 7px;
  padding: 0 12px;
  border: 1px solid #cfcbd5;
  border-radius: 4px;
  background: #fff;
  color: ${text};
  font: inherit;
  font-weight: 400;
  outline: none;

  &:focus {
    border-color: ${purple};
    box-shadow: 0 0 0 2px rgba(77, 37, 141, 0.12);
  }
`;

export const Select = styled.select`
  display: block;
  width: 100%;
  height: 42px;
  box-sizing: border-box;
  margin-top: 7px;
  padding: 0 10px;
  border: 1px solid #cfcbd5;
  border-radius: 4px;
  background: #fff;
  color: ${text};
  font: inherit;
  font-weight: 400;
  outline: none;

  &:focus {
    border-color: ${purple};
    box-shadow: 0 0 0 2px rgba(77, 37, 141, 0.12);
  }
`;

export const Subsection = styled.div`
  margin-top: 25px;
  padding-top: 20px;
  border-top: 1px solid ${border};
`;

export const ToggleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 10px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

export const Toggle = styled.label<{ active?: boolean }>`
  min-height: 54px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 12px;
  box-sizing: border-box;
  border: 1px solid ${props => (props.active ? '#bda9d9' : border)};
  border-radius: 4px;
  background: ${props => (props.active ? purpleLight : '#fff')};
  color: ${text};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  input {
    accent-color: ${purple};
  }
  span {
    display: block;
    font-weight: 400;
    color: ${muted};
    font-size: 12px;
    margin-top: 2px;
  }
`;

export const ErrorText = styled.div`
  margin-top: 14px;
  padding: 10px 12px;
  border-left: 3px solid #bb3a3a;
  background: #fff5f5;
  color: #8d2727;
  font-size: 13px;
  line-height: 1.4;
`;

export const ResultPanel = styled(Section)`
  position: sticky;
  top: 18px;

  @media (max-width: 900px) {
    position: static;
  }
`;

export const Total = styled.div`
  margin: 8px 0 18px;
  padding: 16px;
  background: ${purpleLight};
  border-left: 4px solid ${purple};
  .label {
    color: ${muted};
    font-size: 13px;
  }
  strong {
    display: block;
    margin-top: 4px;
    color: ${purple};
    font-size: 30px;
    line-height: 1.1;
  }
`;

export const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 0;
  border-bottom: 1px solid #efedf1;
  font-size: 13px;
  color: ${muted};
  strong {
    color: ${text};
    font-weight: 600;
    white-space: nowrap;
  }
`;

export const CoverageTable = styled.div`
  margin-top: 12px;
  .coverage {
    padding: 10px 0;
    border-bottom: 1px solid #efedf1;
  }
  .coverage-head {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    font-size: 13px;
    color: ${text};
  }
  .coverage-meta {
    margin-top: 4px;
    color: ${muted};
    font-size: 12px;
  }
`;

export const PrimaryButton = styled.button`
  width: 100%;
  min-height: 44px;
  margin-top: 20px;
  padding: 10px 16px;
  border: 0;
  border-radius: 4px;
  background: ${purple};
  color: #fff;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    filter: brightness(1.08);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const SecondaryButton = styled.button`
  min-height: 40px;
  padding: 8px 14px;
  border: 1px solid #cfcbd5;
  border-radius: 4px;
  background: #fff;
  color: ${purple};
  font: inherit;
  font-weight: 600;
  cursor: pointer;
`;

export const ModalBackdrop = styled.div`
  position: fixed;
  z-index: 10;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
  background: rgba(31, 26, 38, 0.52);
`;

export const Modal = styled.div`
  width: 100%;
  max-width: 520px;
  max-height: calc(100vh - 40px);
  overflow: auto;
  padding: 24px;
  box-sizing: border-box;
  border-radius: 8px;
  background: #fff;
  h2 {
    margin: 0 0 8px;
    font-size: 20px;
  }
  p {
    margin: 0 0 18px;
    color: ${muted};
    font-size: 14px;
    line-height: 1.45;
  }
`;

export const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  @media (max-width: 480px) {
    flex-direction: column-reverse;
    button {
      width: 100%;
    }
  }
`;

export const Notice = styled.div<{ success?: boolean }>`
  margin-top: 14px;
  padding: 12px;
  border: 1px solid ${props => (props.success ? '#b7d8be' : '#e6bebe')};
  background: ${props => (props.success ? '#f1faf3' : '#fff5f5')};
  color: ${props => (props.success ? '#246b34' : '#8d2727')};
  font-size: 13px;
  line-height: 1.45;
`;

export const Footer = styled.footer`
  width: 100%;
  max-width: 1120px;
  margin: 18px auto 0;
  color: ${muted};
  font-size: 12px;
  line-height: 1.45;
`;
